import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, 
  Users, 
  Building, 
  Mail, 
  MapPin, 
  Briefcase,
  Phone,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contacts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const limit = 20;

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      if (search.trim()) params.append('search', search.trim());
      if (companyFilter && companyFilter !== 'all') params.append('company', companyFilter);
      
      const res = await fetch(`${API_URL}/api/contacts?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setContacts(data.contacts || []);
        setTotal(data.total || 0);
        setCompanies(data.companies || []);
      }
    } catch (e) {
      console.error('Error fetching contacts:', e);
    } finally {
      setLoading(false);
    }
  }, [page, search, companyFilter]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const fetchContactDetail = async (contactId) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API_URL}/api/contacts/${contactId}`, { credentials: 'include' });
      if (res.ok) setSelectedContact(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchContacts();
  };

  const totalPages = Math.ceil(total / limit);

  const getGradeBadge = (grade) => {
    const styles = {
      'Hot Deal': 'bg-orange-500/15 text-orange-600 border-orange-500/30',
      'SQL': 'bg-success/15 text-success border-success/30',
      'Warm Lead': 'bg-yellow-500/15 text-amber-600 border-yellow-500/30',
      'Cold Lead': 'bg-blue-500/15 text-blue-600 border-blue-500/30',
      'Disqualified': 'bg-destructive/15 text-destructive border-destructive/30'
    };
    return styles[grade] || 'bg-secondary text-muted-foreground';
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="contacts-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Contacts</h1>
            <p className="text-muted-foreground mt-1">{total} contacts extracted from calls</p>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex gap-3">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-background border-border"
                data-testid="contact-search"
              />
            </div>
            <Button type="submit" variant="secondary" data-testid="contact-search-btn">
              Search
            </Button>
          </form>
          <Select value={companyFilter} onValueChange={(v) => { setCompanyFilter(v); setPage(1); }}>
            <SelectTrigger className="w-48 bg-background border-border" data-testid="company-filter">
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Contact List */}
          <div className="lg:col-span-2 space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : contacts.length === 0 ? (
              <Card className="bg-card border-border/50">
                <CardContent className="py-16 text-center">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No contacts found</p>
                  <p className="text-sm text-muted-foreground mt-1">Contacts are automatically extracted when calls are analyzed</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {contacts.map((contact) => (
                  <Card 
                    key={contact.contact_id || contact.email || contact.full_name}
                    className={`bg-card border-border/50 cursor-pointer transition-colors hover:border-primary/30 ${
                      selectedContact?.contact_id === contact.contact_id ? 'border-primary/50 bg-primary/5' : ''
                    }`}
                    onClick={() => fetchContactDetail(contact.contact_id)}
                    data-testid={`contact-card-${contact.contact_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                            <span className="text-sm font-bold text-primary">
                              {(contact.full_name || '?')[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{contact.full_name || 'Unknown'}</p>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              {contact.job_title && (
                                <span className="flex items-center gap-1">
                                  <Briefcase className="w-3 h-3" /> {contact.job_title}
                                </span>
                              )}
                              {contact.company_name && (
                                <span className="flex items-center gap-1">
                                  <Building className="w-3 h-3" /> {contact.company_name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {contact.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {contact.email}
                            </span>
                          )}
                          {contact.country && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {contact.country}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {(contact.call_ids || []).length}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4">
                    <p className="text-sm text-muted-foreground">
                      Page {page} of {totalPages} ({total} contacts)
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setPage(p => Math.max(1, p - 1))} 
                        disabled={page <= 1}
                        data-testid="prev-page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
                        disabled={page >= totalPages}
                        data-testid="next-page"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Contact Detail Panel */}
          <div>
            {selectedContact ? (
              <Card className="bg-card border-border/50 sticky top-6">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Contact Details</CardTitle>
                    <button onClick={() => setSelectedContact(null)} className="text-muted-foreground hover:text-foreground">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingDetail ? (
                    <Loader2 className="w-5 h-5 animate-spin text-primary mx-auto" />
                  ) : (
                    <>
                      {/* Avatar & Name */}
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                          <span className="text-xl font-bold text-primary">
                            {(selectedContact.full_name || '?')[0].toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-heading text-lg font-bold text-foreground">{selectedContact.full_name || 'Unknown'}</p>
                          {selectedContact.job_title && (
                            <p className="text-sm text-muted-foreground">{selectedContact.job_title}</p>
                          )}
                        </div>
                      </div>

                      {/* Info Fields */}
                      <div className="space-y-3 pt-2">
                        {selectedContact.company_name && (
                          <div className="flex items-center gap-3 text-sm">
                            <Building className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="text-foreground">{selectedContact.company_name}</span>
                          </div>
                        )}
                        {selectedContact.email && (
                          <div className="flex items-center gap-3 text-sm">
                            <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                            <a href={`mailto:${selectedContact.email}`} className="text-primary hover:underline">{selectedContact.email}</a>
                          </div>
                        )}
                        {selectedContact.country && (
                          <div className="flex items-center gap-3 text-sm">
                            <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="text-foreground">{selectedContact.country}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-3 text-sm">
                          <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
                          <span className="text-foreground">{(selectedContact.call_ids || []).length} linked call(s)</span>
                        </div>
                      </div>

                      {/* Linked Calls */}
                      {selectedContact.linked_calls?.length > 0 && (
                        <div className="pt-3 border-t border-border/50">
                          <p className="text-xs text-muted-foreground mb-2 font-medium">LINKED CALLS</p>
                          <div className="space-y-2">
                            {selectedContact.linked_calls.map((call) => (
                              <button
                                key={call.call_id}
                                onClick={() => navigate(`/report/${call.call_id}`)}
                                className="w-full flex items-center justify-between p-2 rounded-lg bg-secondary/30 border border-border/50 hover:border-primary/30 transition-colors text-left"
                                data-testid={`linked-call-${call.call_id}`}
                              >
                                <div>
                                  <p className="text-sm font-medium text-foreground truncate">{call.title}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {call.call_date ? new Date(call.call_date).toLocaleDateString() : ''}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {call.grade && (
                                    <Badge className={`${getGradeBadge(call.grade)} border text-xs`}>{call.grade}</Badge>
                                  )}
                                  <ExternalLink className="w-3 h-3 text-muted-foreground" />
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Metadata */}
                      <div className="pt-3 border-t border-border/50 text-xs text-muted-foreground">
                        {selectedContact.created_at && <p>Added: {new Date(selectedContact.created_at).toLocaleDateString()}</p>}
                        {selectedContact.last_seen && <p>Last seen: {new Date(selectedContact.last_seen).toLocaleDateString()}</p>}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-card border-border/50">
                <CardContent className="py-16 text-center">
                  <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Select a contact to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
